import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Wand2, Target, MessageSquareQuote, Link2, ShieldCheck, CheckCircle2, XCircle, Send, RotateCcw, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchBrief, generateBrief, generateSeoKeywords, updateBrief, updateBriefStatus } from "@/lib/contentOps/api";
import type { ArticleRow, ContentBrief, BriefStatus } from "@/lib/contentOps/types";

interface Props { article: ArticleRow; onArticleChange: (a: ArticleRow) => void }

const STATUS_META: Record<BriefStatus, { label: string; color: string; bg: string }> = {
  draft:      { label: "Draft",       color: "hsl(0 0% 70%)",    bg: "hsl(0 0% 14%)" },
  in_review:  { label: "In review",   color: "hsl(38 92% 65%)",  bg: "hsl(38 92% 45% / 0.15)" },
  approved:   { label: "Approved",    color: "hsl(142 70% 60%)", bg: "hsl(142 70% 35% / 0.15)" },
  rejected:   { label: "Rejected",    color: "hsl(0 75% 65%)",   bg: "hsl(0 75% 45% / 0.15)" },
};

const Section = ({ title, icon: Icon, children }: { title: string; icon: typeof Sparkles; children: React.ReactNode }) => (
  <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} style={{ color: "hsl(207 68% 60%)" }} />
      <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: "hsl(0 0% 85%)" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const BriefTab = ({ article, onArticleChange }: Props) => {
  const [brief, setBrief] = useState<ContentBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [persona, setPersona] = useState("");
  const [notes, setNotes] = useState("");
  const [seedKws, setSeedKws] = useState("");
  const [reviewerNotes, setReviewerNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [suggestingKw, setSuggestingKw] = useState(false);

  const suggestKeywords = async () => {
    setSuggestingKw(true);
    try {
      const res = await generateSeoKeywords({ article_id: article.id });
      const all = Array.from(new Set([res.primary_keyword, ...res.secondary_keywords, ...res.long_tail_keywords].filter(Boolean)));
      if (brief) {
        await persist({
          primary_keyword: res.primary_keyword,
          target_keywords: all,
        });
      } else {
        setSeedKws(all.join(", "));
      }
      toast.success(`Suggested ${all.length} keywords`, { description: res.notes ?? undefined });
    } catch (e) {
      toast.error("Keyword suggestion failed", { description: (e as Error).message });
    } finally {
      setSuggestingKw(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const b = await fetchBrief(article.id);
      setBrief(b);
      setReviewerNotes(b?.reviewer_notes ?? "");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [article.id]);

  const run = async () => {
    if (!article.title.trim()) { toast.error("Add a working title first"); return; }
    setGenerating(true);
    try {
      const { brief: b } = await generateBrief({
        article_id: article.id,
        topic: article.title,
        target_keywords: seedKws.split(",").map((s) => s.trim()).filter(Boolean),
        persona: persona || undefined,
        notes: notes || undefined,
      });
      setBrief(b);
      onArticleChange({ ...article, status: "brief", target_keywords: b.target_keywords });
      toast.success("Brief generated");
    } catch (e) {
      toast.error("Brief generation failed", { description: (e as Error).message });
    } finally {
      setGenerating(false);
    }
  };

  const changeStatus = async (status: BriefStatus, includeNotes = false) => {
    if (!brief) return;
    setSavingStatus(true);
    try {
      const updated = await updateBriefStatus(brief.id, status, includeNotes ? reviewerNotes : null);
      setBrief(updated);
      toast.success(`Brief marked ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error("Status update failed", { description: (e as Error).message });
    } finally {
      setSavingStatus(false);
    }
  };

  const persist = async (patch: Partial<ContentBrief>) => {
    if (!brief) return;
    const optimistic = { ...brief, ...patch } as ContentBrief;
    setBrief(optimistic);
    try {
      const saved = await updateBrief(brief.id, patch);
      setBrief(saved);
    } catch (e) {
      toast.error("Save failed", { description: (e as Error).message });
      setBrief(brief);
    }
  };

  const briefStatus: BriefStatus = (brief?.status as BriefStatus) ?? "draft";
  const meta = STATUS_META[briefStatus];

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-1 space-y-4">
        <Section title="Generate brief" icon={Sparkles}>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>Seed keywords (comma-separated)</label>
              <Input value={seedKws} onChange={(e) => setSeedKws(e.target.value)} placeholder="cold calling, b2b lead gen"
                className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>Target persona (optional)</label>
              <Input value={persona} onChange={(e) => setPersona(e.target.value)} placeholder="US SMB owner, sales-led"
                className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>Editor notes</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                placeholder="Angle, must-include points, things to avoid…"
                className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
            <Button onClick={run} disabled={generating} className="w-full"
              style={{ background: "hsl(207 68% 50%)", color: "white" }}>
              {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              {brief ? "Regenerate brief" : "Generate brief"}
            </Button>
            <Button onClick={suggestKeywords} disabled={suggestingKw} variant="outline"
              className="w-full border" style={{ borderColor: "hsl(207 68% 40%)", color: "hsl(207 68% 75%)", background: "transparent" }}>
              {suggestingKw ? <Loader2 size={14} className="animate-spin mr-1" /> : <Wand2 size={14} className="mr-1" />}
              Suggest keywords
            </Button>
            <p className="text-[11px]" style={{ color: "hsl(0 0% 45%)" }}>
              Regenerating creates a new revision and replaces the active brief.
            </p>
          </div>
        </Section>

        {brief && (
          <Section title="Review & approval" icon={ShieldCheck}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: "hsl(0 0% 55%)" }}>Status</span>
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
              </div>

              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>Reviewer notes</label>
                <Textarea value={reviewerNotes} onChange={(e) => setReviewerNotes(e.target.value)} rows={3}
                  placeholder="What needs to change before approval?"
                  className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {briefStatus === "draft" && (
                  <Button onClick={() => changeStatus("in_review")} disabled={savingStatus} className="col-span-2"
                    style={{ background: "hsl(38 92% 50%)", color: "white" }}>
                    <Send size={13} className="mr-1" /> Submit for review
                  </Button>
                )}
                {briefStatus === "in_review" && (
                  <>
                    <Button onClick={() => changeStatus("approved")} disabled={savingStatus}
                      style={{ background: "hsl(142 70% 40%)", color: "white" }}>
                      <CheckCircle2 size={13} className="mr-1" /> Approve
                    </Button>
                    <Button onClick={() => changeStatus("rejected", true)} disabled={savingStatus}
                      style={{ background: "hsl(0 75% 45%)", color: "white" }}>
                      <XCircle size={13} className="mr-1" /> Reject
                    </Button>
                  </>
                )}
                {(briefStatus === "approved" || briefStatus === "rejected") && (
                  <Button onClick={() => changeStatus("draft")} disabled={savingStatus} variant="outline"
                    className="col-span-2 border" style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)", background: "transparent" }}>
                    <RotateCcw size={13} className="mr-1" /> Reopen to draft
                  </Button>
                )}
              </div>

              {brief.reviewed_at && (
                <p className="text-[11px]" style={{ color: "hsl(0 0% 45%)" }}>
                  Last review: {new Date(brief.reviewed_at).toLocaleString()}
                </p>
              )}

              <p className="text-[11px] leading-relaxed" style={{ color: "hsl(0 0% 50%)" }}>
                Draft generation is blocked until the brief is <strong style={{ color: "hsl(142 70% 60%)" }}>Approved</strong>.
              </p>
            </div>
          </Section>
        )}
      </div>


      <div className="lg:col-span-2 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-48" style={{ color: "hsl(0 0% 50%)" }}>
            <Loader2 size={18} className="animate-spin mr-2" /> Loading brief…
          </div>
        ) : !brief ? (
          <div className="rounded-xl border p-12 text-center"
            style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
            <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>No brief yet</p>
            <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>Generate one from the panel on the left to start the editorial pipeline.</p>
          </div>
        ) : (
          <>
            <Section title="Strategy" icon={Target}>
              <div className="grid sm:grid-cols-2 gap-4 text-sm" style={{ color: "hsl(0 0% 80%)" }}>
                <div data-field="primary_keyword" tabIndex={-1} className="outline-none">
                  <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Primary keyword</div>
                  <Input
                    value={brief.primary_keyword ?? ""}
                    onChange={(e) => setBrief({ ...brief, primary_keyword: e.target.value })}
                    onBlur={(e) => persist({ primary_keyword: e.target.value.trim() || null })}
                    placeholder="e.g. cold calling services"
                    className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Search intent</div>
                  <Select value={brief.search_intent ?? "none"} onValueChange={(v) => persist({ search_intent: v === "none" ? null : v })}>
                    <SelectTrigger style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}>
                      <SelectValue placeholder="Choose intent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">, </SelectItem>
                      <SelectItem value="informational">Informational</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="transactional">Transactional</SelectItem>
                      <SelectItem value="navigational">Navigational</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2" data-field="objective" tabIndex={-1}>
                  <div className="text-[11px] uppercase tracking-wide mb-1 flex items-center gap-1" style={{ color: "hsl(0 0% 50%)" }}>
                    <Target size={11} /> Objective
                  </div>
                  <Textarea
                    rows={2}
                    value={brief.objective ?? ""}
                    onChange={(e) => setBrief({ ...brief, objective: e.target.value })}
                    onBlur={(e) => persist({ objective: e.target.value.trim() || null })}
                    placeholder="What will the reader walk away able to do?"
                    className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                </div>
                <div data-field="target_audience" tabIndex={-1}>
                  <div className="text-[11px] uppercase tracking-wide mb-1 flex items-center gap-1" style={{ color: "hsl(0 0% 50%)" }}>
                    <Users size={11} /> Target audience
                  </div>
                  <Input
                    value={brief.target_audience ?? ""}
                    onChange={(e) => setBrief({ ...brief, target_audience: e.target.value })}
                    onBlur={(e) => persist({ target_audience: e.target.value.trim() || null })}
                    placeholder="US SMB owner, sales-led"
                    className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                </div>
                <div data-field="quick_answer" tabIndex={-1}>
                  <div className="text-[11px] uppercase tracking-wide mb-1 flex items-center gap-1" style={{ color: "hsl(0 0% 50%)" }}>
                    <Zap size={11} /> Quick answer (AI snippet)
                  </div>
                  <Textarea
                    rows={2}
                    value={brief.quick_answer ?? ""}
                    onChange={(e) => setBrief({ ...brief, quick_answer: e.target.value })}
                    onBlur={(e) => persist({ quick_answer: e.target.value.trim() || null })}
                    placeholder="40-60 word direct answer for AI search…"
                    className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                </div>
                <div className="sm:col-span-2" data-field="angle" tabIndex={-1}>
                  <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Editorial angle</div>
                  <Textarea
                    rows={2}
                    value={brief.angle ?? ""}
                    onChange={(e) => setBrief({ ...brief, angle: e.target.value })}
                    onBlur={(e) => persist({ angle: e.target.value.trim() || null })}
                    placeholder="What makes this article uniquely valuable vs competitors?"
                    className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                </div>
                <div className="sm:col-span-2" data-field="cta_strategy" tabIndex={-1}>
                  <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>CTA recommendation</div>
                  <Textarea
                    rows={2}
                    value={brief.cta_strategy ?? ""}
                    onChange={(e) => setBrief({ ...brief, cta_strategy: e.target.value })}
                    onBlur={(e) => persist({ cta_strategy: e.target.value.trim() || null })}
                    placeholder="Primary CTA, where it appears, supporting offer…"
                    className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                </div>
                <div className="sm:col-span-2" data-field="secondary_keywords" tabIndex={-1}>
                  <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Target keywords (comma-separated)</div>
                  <Input
                    value={brief.target_keywords.join(", ")}
                    onChange={(e) => setBrief({ ...brief, target_keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    onBlur={(e) => {
                      const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                      persist({ target_keywords: arr });
                    }}
                    placeholder="cold calling, b2b lead gen, appointment setting"
                    className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {brief.target_keywords.map((k) => (
                      <span key={k} className="rounded-full px-2 py-0.5 text-[11px]"
                        style={{ background: "hsl(207 68% 50% / 0.15)", color: "hsl(207 68% 75%)" }}>{k}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Section>

            <Section title="Outline" icon={MessageSquareQuote}>
              <ol className="space-y-3">
                {brief.outline.map((s, i) => (
                  <li key={i} className="rounded-lg border p-3" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 14%)" }}>
                    <div className="text-sm font-medium" style={{ color: "hsl(0 0% 90%)" }}>{i + 1}. {s.h2}</div>
                    {s.summary && <p className="text-xs mt-1 leading-relaxed" style={{ color: "hsl(0 0% 60%)" }}>{s.summary}</p>}
                    {s.h3 && s.h3.length > 0 && (
                      <ul className="mt-2 ml-4 list-disc text-xs space-y-0.5" style={{ color: "hsl(0 0% 65%)" }}>
                        {s.h3.map((h, j) => <li key={j}>{h}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            </Section>

            {brief.competitor_urls.length > 0 && (
              <Section title="Competitor SERP" icon={Link2}>
                <ul className="space-y-1">
                  {brief.competitor_urls.map((u) => (
                    <li key={u}>
                      <a href={u} target="_blank" rel="noreferrer" className="text-xs hover:underline break-all"
                        style={{ color: "hsl(207 68% 65%)" }}>{u}</a>
                    </li>
                  ))}
                </ul>
                {brief.serp_summary?.content_gaps && Array.isArray(brief.serp_summary.content_gaps) && (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Content gaps</div>
                    <ul className="ml-4 list-disc text-xs space-y-0.5" style={{ color: "hsl(0 0% 70%)" }}>
                      {(brief.serp_summary.content_gaps as string[]).map((g) => <li key={g}>{g}</li>)}
                    </ul>
                  </div>
                )}
              </Section>
            )}

            <p className="text-[11px]" style={{ color: "hsl(0 0% 40%)" }}>
              Generated with {brief.ai_model ?? "AI"} · {new Date(brief.created_at).toLocaleString()}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default BriefTab;
